import { ArrowRightToLine, Volume2 } from "lucide-react"
import {
  Handle,
  NodeResizeControl,
  Position,
  ResizeControlVariant,
  useStore,
  type ControlLinePosition,
  type ControlPosition,
  type Node,
  type NodeProps,
} from "@xyflow/react"
import { FlowNodePlayer } from "@/components/flow/flow-node-player"
import { flowNodeKindIcon, FlowNodeTitle, useFlowNodeTitle } from "@/components/flow/flow-node-title"
import { useFlowVersions } from "@/components/flow/flow-versions-context"
import {
  AssetDownloadOverlayButton,
  AssetOverlayTopLeft,
  AssetVersionsBadge,
} from "@/components/media/asset-media-overlay"
import { AssetPreviewGenerating } from "@/components/media/asset-preview-busy"
import { AudioPreviewPlayer } from "@/components/ui/audio-preview-player"
import { Button } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import { downloadImage, filenameFromUrl } from "@/lib/download-image"
import { chainNestedInputsHint, chainNodeCanBeInput, chainStarterInputWarning } from "@/lib/flow-chain-starter"
import {
  FLOW_NODE_MAX_LONG_SIDE,
  FLOW_NODE_MIN_LONG_SIDE,
  clampFlowNodeRatio,
  flowNodeAspectRatio,
} from "@/lib/flow-node-size"
import { cn } from "@/lib/utils"
import {
  flowNodeAudioOutput,
  flowNodeImageOutput,
  flowNodePending,
  flowNodePlaysVideo,
  flowNodeSourceHandles,
  flowNodeTargetHandles,
  flowNodeVideoOutput,
  type FlowNodeRow,
} from "@/types/flow"

export type FlowCardData = FlowNodeRow & { mentioned?: boolean }

export type FlowCardNode = Node<FlowCardData, "flowCard">

/** Ниже этого масштаба нода слишком мелкая, чтобы тянуть её за край. */
const RESIZE_MIN_ZOOM = 0.3

/** Толщина невидимых зон ресайза на экране; при отдалении зоны не тоньше этого. */
const RESIZE_EDGE_PX = 10
const RESIZE_CORNER_PX = 16
const RESIZE_EDGES: ControlLinePosition[] = ["top", "right", "bottom", "left"]
const RESIZE_CORNERS: ControlPosition[] = ["top-left", "top-right", "bottom-left", "bottom-right"]

function sourceHandleLabel(handleId: string) {
  if (handleId === "entity") return "edges.entityOutput"
  if (handleId === "video") return "edges.videoOutput"
  if (handleId === "audio") return "edges.audioOutput"
  return "edges.output"
}

function targetHandleLabel(handleId: string) {
  if (handleId === "links") return "edges.shotInput"
  if (handleId === "video") return "edges.videoInput"
  return "edges.input"
}

/** Зум ступенями по 0.25: анимация генерации пересчитывает разрешение канваса, а не каждый тик зума. */
function generationResolutionScale(zoom: number) {
  return Math.min(2, Math.max(0.25, Math.round(zoom * 4) / 4))
}

/** Ресайз за любой край или угол без видимых ручек — подсказывает только курсор. Противоположная сторона стоит на месте. */
function FlowNodeResizeZones({ ratio }: { ratio: number }) {
  const edgeScale = useStore((s) => Math.max(1, Math.round(4 / s.transform[2]) / 4))
  const edge = RESIZE_EDGE_PX * edgeScale
  const limits = {
    keepAspectRatio: true,
    minWidth: FLOW_NODE_MIN_LONG_SIDE * Math.min(1, ratio),
    maxWidth: FLOW_NODE_MAX_LONG_SIDE * Math.min(1, ratio),
  }
  return (
    <>
      {RESIZE_EDGES.map((position) => (
        <NodeResizeControl
          key={position}
          variant={ResizeControlVariant.Line}
          position={position}
          className="flow-resize-zone"
          style={position === "left" || position === "right" ? { width: edge } : { height: edge }}
          {...limits}
        />
      ))}
      {RESIZE_CORNERS.map((position) => (
        <NodeResizeControl
          key={position}
          position={position}
          className="flow-resize-zone"
          style={{ width: RESIZE_CORNER_PX, height: RESIZE_CORNER_PX }}
          {...limits}
        />
      ))}
    </>
  )
}

export function FlowNodeCard({ id, data, selected, draggable }: NodeProps<FlowCardNode>) {
  const { t } = useTranslation("flow")
  const versions = useFlowVersions()
  const titleEdit = useFlowNodeTitle()
  const chainInputs = Boolean(titleEdit?.chainInputs)
  const starter = useStore((state) => {
    if (!chainInputs) return false
    let incoming = false
    let outgoing = false
    for (const edge of state.edges) {
      if (edge.target === id) incoming = true
      if (edge.source === id) outgoing = true
    }
    return outgoing && !incoming
  })
  const pending = flowNodePending(data)
  const versionCount = data.version_count ?? 0
  const playsVideo = flowNodePlaysVideo(data)
  const audioOutput = flowNodeAudioOutput(data)
  const preview = audioOutput ? null : data.media_thumb_url || (!playsVideo ? data.media_url : null)
  const Icon = flowNodeKindIcon(data.kind)
  const ratio = clampFlowNodeRatio(flowNodeAspectRatio(data))
  const resizable = useStore((s) => s.transform[2] >= RESIZE_MIN_ZOOM) && draggable
  const selectedCount = useStore((state) => {
    let count = 0
    for (const node of state.nodes) if (node.selected) count += 1
    return count
  })
  const resolutionScale = useStore((s) => generationResolutionScale(s.transform[2]))

  const imageOutput = flowNodeImageOutput(data)
  const videoOutput = flowNodeVideoOutput(data)
  const sourceHandles = flowNodeSourceHandles(data)
  const noteKey = unboundNoteKey(data)
  const note = noteKey ? t(noteKey) : null
  const showVersions = Boolean(versions) && versionCount > 1
  const downloadUrl = !pending && (imageOutput || videoOutput) ? data.download_url : null
  const chainInput = Boolean(data.chain_input)
  const canEditInput = Boolean(titleEdit?.canEdit)
  const canBeInput = chainNodeCanBeInput(data)
  const inputWarning = chainStarterInputWarning(data, starter, pending)
  const nestedInputsHint = chainNestedInputsHint(data, starter)
  const concealInputMedia = starter && chainInput && canBeInput
  const showCenterInputButton = starter && canEditInput && canBeInput
  const inputButtonLabel = chainInput ? t("chain.cancelInput") : t("chain.makeInput")
  const toggleChainInput = () => titleEdit?.setChainInput(data.id, !chainInput)
  return (
    <div className="group/flow-node relative">
      <FlowNodeTitle node={data} />
      {sourceHandles.map((handleId) => (
        <Handle
          key={`source:${handleId}`}
          type="source"
          position={Position.Right}
          id={handleId}
          className="flow-handle"
          aria-label={t(sourceHandleLabel(handleId))}
        />
      ))}
      {concealInputMedia
        ? null
        : flowNodeTargetHandles(data).map((handleId) => (
            <Handle
              key={`target:${handleId}`}
              type="target"
              position={Position.Left}
              id={handleId}
              className="flow-handle"
              aria-label={t(targetHandleLabel(handleId))}
            />
          ))}
      {concealInputMedia ? (
        <span className="flow-chain-input-label inline-flex items-center gap-1 rounded-full bg-ui-accent px-2 py-0.5 text-[11px] leading-4 whitespace-nowrap text-ui-on-accent">
          <ArrowRightToLine className="h-3 w-3 shrink-0" aria-hidden />
          {t("chain.input")}
        </span>
      ) : null}
      {resizable && selected ? <FlowNodeResizeZones ratio={ratio} /> : null}
      <div
        className={cn(
          "overflow-hidden rounded-lg border border-ui-border bg-ui-surface shadow-sm",
          (selected || data.mentioned) && "ring-2 ring-ui-accent",
        )}
      >
        <div className="relative bg-ui-surface-sunken" style={{ aspectRatio: String(ratio) }}>
          <div className="absolute inset-0 flex items-center justify-center">
            {concealInputMedia ? (
              pending ? null : (
                <div
                  className={cn(
                    "flex flex-col items-center gap-2 px-3 text-center text-ui-text-muted",
                    showCenterInputButton && !pending && "pb-14",
                  )}
                >
                  <ArrowRightToLine className="h-5 w-5" />
                  <span className="text-[11px] text-ui-text-secondary">{t("chain.inputEmpty")}</span>
                  <span className="text-[11px]">{t("chain.inputEmptyHint")}</span>
                </div>
              )
            ) : playsVideo ? (
              <FlowNodePlayer data={data} selected={selected && selectedCount === 1} />
            ) : preview ? (
              <img className="absolute inset-0 h-full w-full object-cover" src={preview} alt="" />
            ) : audioOutput && data.media_url ? (
              <div
                className="nodrag nopan nowheel flex w-full items-center px-3"
                onPointerDown={(event) => event.stopPropagation()}
              >
                <AudioPreviewPlayer src={data.media_url} className="min-w-0 flex-1" />
              </div>
            ) : pending ? null : inputWarning ? (
              <div
                className={cn(
                  "flex flex-col items-center gap-2 px-3 text-center",
                  showCenterInputButton && !pending && "pb-14",
                )}
              >
                <Icon className="h-5 w-5 text-ui-warning" />
                <span className="text-[11px] text-ui-warning">{t("chain.starterChoice")}</span>
              </div>
            ) : nestedInputsHint ? (
              <div className="flex flex-col items-center gap-2 px-3 text-center">
                <Icon className="h-5 w-5 text-ui-warning" />
                <span className="text-[11px] text-ui-warning">{t("chain.nestedInputs")}</span>
              </div>
            ) : (
              <div
                className={cn(
                  "flex flex-col items-center gap-2 px-3 text-center text-ui-text-muted",
                  showCenterInputButton && !pending && "pb-14",
                )}
              >
                {audioOutput ? <Volume2 className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                {note ? <span className="text-[11px]">{note}</span> : null}
              </div>
            )}
          </div>
          <AssetPreviewGenerating
            active={pending}
            status={data.generation_status === "queued" ? "queued" : "processing"}
            onMedia={!concealInputMedia && (playsVideo || Boolean(preview))}
            seedKey={`flow-node-${data.id}`}
            resolutionScale={resolutionScale}
          />
          {!concealInputMedia && (showVersions || downloadUrl) ? (
            <div
              className="nodrag nopan pointer-events-none absolute inset-0"
              onPointerDown={(event) => event.stopPropagation()}
            >
              <AssetOverlayTopLeft>
                {versions && showVersions ? (
                  <AssetVersionsBadge
                    count={versionCount}
                    title={t("versions.title")}
                    onOpen={() => versions.openVersions(data.id)}
                  />
                ) : null}
                {downloadUrl ? (
                  <AssetDownloadOverlayButton
                    title={t("node.download")}
                    className="opacity-0 transition-opacity group-hover/flow-node:opacity-100 focus-visible:opacity-100"
                    onClick={() =>
                      void downloadImage(
                        downloadUrl,
                        filenameFromUrl(downloadUrl, downloadBasename(data), videoOutput ? ".mp4" : ".jpg"),
                      )
                    }
                  />
                ) : null}
              </AssetOverlayTopLeft>
            </div>
          ) : null}
          {showCenterInputButton && !pending ? (
            <div className="absolute inset-x-0 bottom-0 z-30 px-3 pb-3">
              <ChainInputButton label={inputButtonLabel} onToggle={toggleChainInput} />
            </div>
          ) : null}
        </div>
        {data.generation_status === "failed" ? (
          <p className="truncate px-2 py-1.5 text-[11px] text-red-600" title={data.generation_error ?? undefined}>
            {data.generation_error || t("node.failed")}
          </p>
        ) : null}
      </div>
    </div>
  )
}

function ChainInputButton({ label, onToggle }: { label: string; onToggle: () => void }) {
  return (
    <Button
      type="button"
      variant="secondary"
      className="nodrag nopan nowheel w-full max-w-full bg-ui-surface-ink hover:bg-ui-surface-ink hover:brightness-110"
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation()
        onToggle()
      }}
    >
      {label}
    </Button>
  )
}

function downloadBasename(data: FlowNodeRow): string {
  return data.title?.trim() || data.shot_label?.trim() || `flow-node-${data.id}`
}

function unboundNoteKey(
  data: FlowNodeRow,
):
  | "node.shotMissing"
  | "node.characterMissing"
  | "node.locationMissing"
  | "node.propMissing"
  | "node.chainMissing"
  | "node.empty"
  | "node.upscaleEmpty"
  | "node.depthEmpty"
  | null {
  if (data.content_type?.startsWith("audio/")) return null
  if (data.kind === "shot") return data.shot_id == null ? "node.shotMissing" : null
  if (data.kind === "character") return data.character_id == null ? "node.characterMissing" : null
  if (data.kind === "location") return data.location_id == null ? "node.locationMissing" : null
  if (data.kind === "prop") return data.prop_id == null ? "node.propMissing" : null
  if (data.kind === "chain") return data.chain_id == null ? "node.chainMissing" : "node.empty"
  if (data.kind === "video_upscale") return "node.upscaleEmpty"
  if (data.kind === "video_depth") return "node.depthEmpty"
  return "node.empty"
}
